import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DesignHuddleService } from '@app/shared/services/design-huddle/design-huddle.service';
import { StoreService } from '@app/feature/store-admin/services/store.service';

/**
 * Upper bound for a MerchLoop project/product title, entered in this dialog. CustomizeProducts.Name
 * is nvarchar(max) and Shopify's own product-title limit is 255 — but per explicit user decision
 * this is pinned to 50 to match Design Huddle's own project_title cap (see toDesignHuddleProjectTitle
 * below), even though that means a title longer than 50 chars is truncated here before it ever
 * reaches DH. (Was 255 from Oct 5 2026 until this change — that fix's own prod-truncation concern,
 * ~6k existing titles >45 chars, applies again now that this is capped below their length.)
 */
export const MAX_PROJECT_TITLE_LENGTH = 50;

/**
 * Design Huddle caps `project_title` at 50 characters (documented on its update-project API).
 * The DH title is a cosmetic label only — MerchLoop stores the project_id, never the title —
 * so callers clamp the title at the DH boundary and keep the full title everywhere else.
 */
export const DH_PROJECT_TITLE_MAX = 50;

export function toDesignHuddleProjectTitle(title: string): string {
  return (title || '').trim().substring(0, DH_PROJECT_TITLE_MAX);
}

@Component({
  selector: 'app-project-titles-dialog',
  templateUrl: './project-titles-dialog.component.html',
  styleUrls: ['./project-titles-dialog.component.css']
})
export class ProjectTitlesDialogComponent implements OnInit {
  title: string;
  message: string;
  products: any[] = [];
  projectTitles: string[] = [];
  disabledProducts: boolean[] = [];
  duplicateFlags: boolean[] = [];
  titleTouched: boolean[] = [];
  isChecking: boolean = false;
  storeId: number;
  isEditMode: boolean = false;
  readonly maxTitleLength = MAX_PROJECT_TITLE_LENGTH;

  /** "<Company Name>" prefix used for the default "<Company> - <Product>" title. Empty until the store loads. */
  companyPrefix: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ProjectTitlesDialogComponent>,
    private designHuddleService: DesignHuddleService,
    private storeService: StoreService
  ) { }

  ngOnInit() {
    this.title = this.data.title;
    this.message = this.data.message;
    this.storeId = this.data.storeId;
    this.products = this.data.products || [];
    this.isEditMode = !!this.data.isEditMode;

    // Initialize project titles.
    // Disabled products already have a saved title — preserve it for display.
    // Edit mode: prefill with the existing project title (capped to maxTitleLength — see capTitle;
    // many saved titles predate the current 50-char convention and can run well past it).
    // Create mode: prefill with the "<Company> - <Product>" convention so a single Tab accepts it.
    // The company prefix arrives asynchronously (see loadCompanyPrefix); until then the default is the product name.
    //
    // [maxlength] on the <input> only restricts the user's OWN typing/pasting — it does nothing
    // when a value is assigned here from TypeScript (ngModel just writes it straight to the DOM),
    // so every title set programmatically in this component must be capped itself or the counter
    // can show "67/50" while the field genuinely holds 67 characters.
    this.projectTitles = this.products.map(p => {
      if (p.disabled || this.isEditMode) {
        return this.capTitle((p.projectTitle || '').trim()) || this.buildDefaultTitle(p);
      }
      return this.buildDefaultTitle(p);
    });

    // Initialize disabled flags per product
    this.disabledProducts = this.products.map(p => !!p.disabled);
    this.duplicateFlags = this.products.map(() => false);
    // In edit mode, mark prefilled fields as touched so the field is immediately valid
    // (no spurious required-error on an already-filled title).
    this.titleTouched = this.products.map((p, i) => {
      if (this.isEditMode && !p.disabled && this.projectTitles[i]) {
        return true;
      }
      return false;
    });

    this.loadCompanyPrefix();
  }

  /** "<prefix> - <product name>", or just the product name when no prefix is known. Capped to
   *  maxTitleLength — a real company+product name combination very often runs well past 50
   *  characters on its own, and this is set via TS, not typed, so [maxlength] never sees it. */
  buildDefaultTitle(product: any): string {
    const productName = (product?.productName || '').trim();
    const full = this.companyPrefix ? `${this.companyPrefix} - ${productName}` : productName;
    return this.capTitle(full);
  }

  /** Hard cap matching the <input>'s [maxlength] — needed wherever a title is set from TypeScript
   *  rather than typed/pasted by the user, since [maxlength] never applies to those assignments. */
  private capTitle(title: string): string {
    return (title || '').substring(0, this.maxTitleLength);
  }

  /**
   * Resolve the company prefix from the store owner's CompanyInfo (GetStoreById includes
   * User.CompanyInfoObj). Falls back to StoreName when that is a real name rather than the
   * Shopify domain (flagship-hosted stores have StoreName == StoreUrl). Create-mode fields the
   * user has not touched are re-defaulted once the prefix is known.
   */
  private loadCompanyPrefix(): void {
    if (!this.storeId) { return; }
    this.storeService.getStoreById(String(this.storeId)).subscribe({
      next: (res: any) => {
        const store = res?.Data?.ResponseData;
        if (!store) { return; }
        const companyName = (store.User?.CompanyInfoObj?.CompanyName || '').trim();
        const storeName = (store.StoreName || '').trim();
        const storeUrl = (store.StoreUrl || '').trim();
        this.companyPrefix = companyName || (storeName && storeName !== storeUrl ? storeName : '');
        if (!this.companyPrefix) { return; }

        this.products.forEach((p, i) => {
          const untouchedCreateField = !this.isEditMode && !this.disabledProducts[i] && !this.titleTouched[i];
          const emptyEditField = this.isEditMode && !this.disabledProducts[i] && !(this.projectTitles[i] || '').trim();
          if (untouchedCreateField || emptyEditField) {
            this.projectTitles[i] = this.buildDefaultTitle(p);
          }
        });
      },
      error: () => {
        // No prefix — the product-name default already in place stays.
      }
    });
  }

  markTouched(index: number): void {
    this.titleTouched[index] = true;
  }

  validateTitle(index: number): void {
    // Typing counts as touching so an async company-prefix load never overwrites user input.
    this.titleTouched[index] = true;
    // Clear duplicate flag when user edits the title
    this.duplicateFlags[index] = false;
  }

  isValid(): boolean {
    return this.projectTitles.every(title => title && title.trim().length > 0)
      && !this.isChecking;
  }

  /** True when, in edit mode, the entered title is the product's own saved title (never a duplicate). */
  private isUnchangedSavedTitle(index: number, title: string): boolean {
    if (!this.isEditMode) { return false; }
    const saved = (this.products[index]?.projectTitle || '').trim().toLowerCase();
    return !!saved && saved === title.trim().toLowerCase();
  }

  onConfirm(): void {
    if (!this.projectTitles.every(title => title && title.trim().length > 0)) {
      return;
    }

    // Get enabled product titles to check.
    // Edit mode: keeping the product's own saved title is skipped — CheckProjectTitleExists has no
    // self-exclusion, so it would otherwise match the product's own CustomizeProducts rows and block Continue.
    const titlesToCheck: { index: number; title: string }[] = [];
    this.projectTitles.forEach((title, i) => {
      if (!this.disabledProducts[i] && title && title.trim() && !this.isUnchangedSavedTitle(i, title)) {
        titlesToCheck.push({ index: i, title: title.trim() });
      }
    });

    if (titlesToCheck.length === 0) {
      this.dialogRef.close({ confirmed: true, projectTitles: this.projectTitles.map(t => this.capTitle(t.trim())) });
      return;
    }

    this.isChecking = true;
    this.duplicateFlags = this.products.map(() => false);

    let completed = 0;
    let hasDuplicate = false;

    titlesToCheck.forEach(item => {
      // Existence check — any other product in the store already using this title is a duplicate.
      this.designHuddleService.checkProjectTitleExists(item.title, this.storeId || 0).subscribe({
        next: (response) => {

          const exists = !!response?.Data?.ResponseData?.Exists;
          if (exists) {
            this.duplicateFlags[item.index] = true;
            hasDuplicate = true;
          }
          completed++;
          if (completed === titlesToCheck.length) {
            this.isChecking = false;
            if (!hasDuplicate) {
              this.dialogRef.close({ confirmed: true, projectTitles: this.projectTitles.map(t => this.capTitle(t.trim())) });
            }
          }
        },
        error: (err) => {

          this.isChecking = false;
          // Don't proceed on error — user must retry
        }
      });
    });
  }

  onCancel(): void {
    this.dialogRef.close({
      confirmed: false
    });
  }
}
